import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { useMoleculeSearch } from '../../hooks/useMoleculeSearch';
import type { MoleculeResult } from '@peruselab/api-client/moleculeSearch';
import { listIsomersByMolecule, type IsomerSummary } from '@peruselab/api-client/isomers';

interface IsomerPickerProps {
  readonly search: ReturnType<typeof useMoleculeSearch>;
  readonly onSelect: (isomerId: string, moleculeId: string, isomerRevision: number) => void;
  // #140: lets Illustration composition reference a bare Molecule (no stereochemistry) instead of
  // always expanding to a specific Isomer. Omitted entirely for Reaction composition, which only
  // ever accepts Isomer participants.
  readonly onSelectMolecule?: (moleculeId: string) => void;
}

type IsomerListState =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly isomers: readonly IsomerSummary[] }
  | { readonly state: 'error'; readonly message: string };

// #127: the isomer picker for composing a Reaction (#125). Reuses the same live search state
// SearchResultsPanel already renders (the current drawing is the query -- there is no separate
// "search by name" mechanism anywhere in this app), but adds the click-to-select interaction
// SearchResultsPanel intentionally doesn't have, since its own job is passive display. A Molecule
// match isn't itself pickable -- a Reaction participant needs a specific Isomer id -- so picking a
// Molecule expands to that Molecule's Isomer(s) for the actual selection.
export function IsomerPicker({ search, onSelect, onSelectMolecule }: IsomerPickerProps) {
  const { t } = useTranslation();
  const { currentResults } = search;
  const [expandedMoleculeId, setExpandedMoleculeId] = useState<string | null>(null);
  const [isomersByMolecule, setIsomersByMolecule] = useState<Readonly<Record<string, IsomerListState>>>({});

  const expandMolecule = useCallback(
    (moleculeId: string) => {
      setExpandedMoleculeId((current) => (current === moleculeId ? null : moleculeId));
      setIsomersByMolecule((current) => {
        if (current[moleculeId]) return current;
        listIsomersByMolecule(moleculeId).then(
          (isomers) => setIsomersByMolecule((state) => ({ ...state, [moleculeId]: { state: 'ready', isomers } })),
          (error: unknown) =>
            setIsomersByMolecule((state) => ({
              ...state,
              [moleculeId]: {
                state: 'error',
                message: error instanceof Error ? error.message : t('isomerPicker.loadIsomersError')
              }
            }))
        );
        return { ...current, [moleculeId]: { state: 'loading' } };
      });
    },
    [t]
  );

  const molecules: readonly MoleculeResult[] = currentResults?.molecules ?? [];

  return (
    <section className="isomer-picker" aria-labelledby="isomer-picker-heading">
      <h2 id="isomer-picker-heading">{t('isomerPicker.heading')}</h2>
      <p>{t('isomerPicker.subheading')}</p>
      {molecules.length === 0 ? <p>{t('isomerPicker.noMatches')}</p> : null}
      <ul className="isomer-picker-list">
        {molecules.map((match) => {
          const isomerState = isomersByMolecule[match.moleculeId];
          return (
            <li key={match.moleculeId}>
              <button type="button" onClick={() => expandMolecule(match.moleculeId)}>
                {t('isomerPicker.moleculeSummary', {
                  moleculeId: match.moleculeId,
                  category: match.category.toLowerCase(),
                  score: match.tanimotoScore.toFixed(2)
                })}
              </button>
              {onSelectMolecule ? (
                <button type="button" onClick={() => onSelectMolecule(match.moleculeId)}>
                  {t('isomerPicker.useMoleculeDirectly')}
                </button>
              ) : null}
              {expandedMoleculeId === match.moleculeId ? (
                <IsomerSubList
                  state={isomerState}
                  onSelect={(isomerId, isomerRevision) => onSelect(isomerId, match.moleculeId, isomerRevision)}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function IsomerSubList({
  state,
  onSelect
}: {
  readonly state: IsomerListState | undefined;
  readonly onSelect: (isomerId: string, isomerRevision: number) => void;
}) {
  const { t } = useTranslation();
  if (!state || state.state === 'loading') {
    return <p className="isomer-picker-status">{t('isomerPicker.loadingIsomers')}</p>;
  }
  if (state.state === 'error') return <p className="isomer-picker-status isomer-picker-error">{state.message}</p>;
  if (state.isomers.length === 0) return <p className="isomer-picker-status">{t('isomerPicker.noIsomers')}</p>;
  return (
    <ul className="isomer-picker-sublist">
      {state.isomers.map((isomer) => (
        <li key={isomer.id}>
          <button type="button" onClick={() => onSelect(isomer.id, isomer.documentRevision)}>
            {t('isomerPicker.isomerSummary', {
              isomerId: isomer.id,
              revision: isomer.documentRevision,
              createdAt: new Date(isomer.createdAt).toLocaleString()
            })}
          </button>
        </li>
      ))}
    </ul>
  );
}
